import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CameraIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { BcmPhoto } from '../../data/photos';
type PhotoGalleryProps = {
  photos: BcmPhoto[];
};

/**
 * Accessible gallery grid with a keyboard-operable lightbox.
 * Arrow keys move between photos, Escape closes, focus returns to the trigger.
 */
export function PhotoGallery({
  photos
}: PhotoGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    const previous = openIndex;
    setOpenIndex(null);
    if (previous !== null) triggerRefs.current[previous]?.focus();
  }, [openIndex]);
  const step = useCallback((delta: number) => {
    setOpenIndex(current => {
      if (current === null) return current;
      return (current + delta + photos.length) % photos.length;
    });
  }, [photos.length]);
  useEffect(() => {
    if (openIndex === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    }
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openIndex, close, step]);
  const active = openIndex === null ? null : photos[openIndex];
  return <>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-id="element-335" data-mp="dtl5bunip067">
        {photos.map((photo, index) => <li key={photo.id} data-id="element-336" data-mp="ivajhj61dtyi" data-mpi={index}>
            <button type="button" ref={node => {
          triggerRefs.current[index] = node;
        }} onClick={() => setOpenIndex(index)} className="group block w-full overflow-hidden rounded-[18px] border-2 border-brand-navy bg-brand-blue text-left transition-transform duration-150 hover:-translate-y-0.5" data-id="element-337" data-mp="2yklktymdddls">
              {photo.src ? <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" className="aspect-square w-full object-cover" data-id="element-338" data-mp="2anyspu8uy9nt" /> : <span className="flex aspect-square w-full flex-col items-center justify-center gap-2 px-3 text-center" data-id="element-339" data-mp="1vrpu8uwsk0cg">
                  <CameraIcon aria-hidden="true" className="h-5 w-5 text-brand-navy" data-id="element-340" data-mp="1rpmis6a4gvr6" />
                  <span className="font-display text-sm font-bold text-brand-navy" data-id="element-341" data-mp="1mrb6lowcu95s">
                    {photo.subject}
                  </span>
                  <span className="font-meta text-[10.5px] uppercase tracking-[0.14em] text-brand-navy" data-id="element-342" data-mp="3m2uee05x9bdv">
                    {photo.album} · real BCM photo
                  </span>
                </span>}
              {photo.caption ? <span className="block border-t-2 border-brand-navy bg-white px-3 py-2 font-meta text-[12px] text-ink" data-id="element-343" data-mp="3tnetvzvcitx7">
                  {photo.caption}
                </span> : null}
              <span className="sr-only" data-id="element-344" data-mp="77u5m213cyal">Open larger view: {photo.alt}</span>
            </button>
          </li>)}
      </ul>

      {active ? <div role="dialog" aria-modal="true" aria-label={active.caption ?? active.subject} className="on-dark fixed inset-0 z-[60] flex flex-col bg-brand-navy/95 p-4 sm:p-8" data-id="element-345" data-mp="35raiora2shjp">
          <div className="flex items-center justify-between" data-id="element-346" data-mp="3fq9rqayr6f78">
            <p className="font-meta text-[12px] uppercase tracking-[0.16em] text-brand-blue" data-id="element-347" data-mp="3jqa47q2akgbn">
              {openIndex !== null ? `${openIndex + 1} of ${photos.length}` : ''}
            </p>
            <button type="button" ref={closeRef} onClick={close} className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-348" data-mp="yy8efju8zkfe">
              <XIcon aria-hidden="true" className="h-6 w-6" data-id="element-349" data-mp="sldv5o7j3iu3" />
              <span className="sr-only" data-id="element-350" data-mp="17h1up01wjhrn">Close photo viewer</span>
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center gap-3 py-4 sm:gap-6" data-id="element-351" data-mp="12ge8qssj5r4a">
            <button type="button" onClick={() => step(-1)} className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-352" data-mp="1gfx40l08t7z6">
              <ChevronLeftIcon aria-hidden="true" className="h-6 w-6" data-id="element-353" data-mp="1q8uouxspj90y" />
              <span className="sr-only" data-id="element-354" data-mp="1l8zwsqx74deb">Previous photo</span>
            </button>

            <figure className="max-h-full w-full max-w-3xl overflow-hidden rounded-[24px] border-2 border-white bg-brand-blue" data-id="element-355" data-mp="1z6omcc27k8pp">
              {active.src ? <img src={active.src} alt={active.alt} className="max-h-[65vh] w-full object-contain" data-id="element-356" data-mp="1v41ptxuldzm6" /> : <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 px-6 text-center" data-id="element-357" data-mp="2iqvr9ub9qizc">
                  <CameraIcon aria-hidden="true" className="h-7 w-7 text-brand-navy" data-id="element-358" data-mp="29sbhjinorjn9" />
                  <p className="font-display text-lg font-bold text-brand-navy" data-id="element-359" data-mp="1v3nnjgthnhzf">{active.subject}</p>
                  <p className="font-meta text-[12px] uppercase tracking-[0.14em] text-brand-navy" data-id="element-360" data-mp="1zudu2xdnhqvp">
                    {active.album} · real BCM photo
                  </p>
                </div>}
              <figcaption className="border-t-2 border-white bg-brand-navy px-4 py-3 font-meta text-[13px] text-white" data-id="element-361" data-mp="1q4b12x6j4cxd">
                {active.caption ?? active.subject}
              </figcaption>
            </figure>

            <button type="button" onClick={() => step(1)} className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-white text-white" data-id="element-362" data-mp="1h45w0tot5qmd">
              <ChevronRightIcon aria-hidden="true" className="h-6 w-6" data-id="element-363" data-mp="3e0s9cb1ilbc0" />
              <span className="sr-only" data-id="element-364" data-mp="3a1tbtbi44gul">Next photo</span>
            </button>
          </div>
        </div> : null}
    </>;
}